import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerSequenceApi,
  type BuyerSequenceSelection,
  type BuyerSequenceStepKey,
  type LogBuyerSequenceRequest,
} from "@/api/buyerSequence";
import { communicationsApi } from "@/api/communications";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { buyerSequenceKeys } from "@/hooks/buyers/buyerSequenceKeys";
import { activityKeys } from "@/pages/activity/hooks/useActivityByOwner";
import { communicationKeys } from "@/pages/communications/hooks/useCommunications";

/**
 * The Outreach tab's one read.
 *
 * `keepPreviousData` so that clicking a rail step or picking a listing does not
 * collapse the whole tab to a skeleton and back: the previous step stays on
 * screen while the next one loads. That makes `isPlaceholderData` load-bearing
 * — while it is true the cards are showing the OLD step's words, so every copy
 * and LOG control is disabled until the new render lands. Copying the previous
 * step's text under the new step's name is exactly the mistake a specialist
 * would never notice.
 */
export function useOutreachState(
  buyerId: string,
  step: BuyerSequenceStepKey,
  selection: BuyerSequenceSelection,
) {
  return useQuery({
    queryKey: buyerSequenceKeys.state(buyerId, step, selection),
    queryFn: () => buyerSequenceApi.getState(buyerId, step, selection),
    enabled: Boolean(buyerId),
    placeholderData: keepPreviousData,
  });
}

/**
 * LOG — writes ordinary communication entries through the server's unchanged
 * create path.
 *
 * Invalidations, inline so `staleAfterSaveGate` can see them:
 *   - `buyerSequenceKeys.stateForBuyer` — the rail's "Sent {date} ✓" marker.
 *   - `communicationKeys.all` — the Communications tab reads its own family,
 *     NOT under `buyerKeys`.
 *   - `buyerKeys.all` — an outreach entry moves exactly the contact stamps any
 *     logged entry moves (G2), and the header's Next step strip, the list row
 *     and the stat cards read those.
 *   - `activityKeys.byOwner` — the Activity tab lists the entry too.
 */
export function useLogOutreach(buyerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LogBuyerSequenceRequest) => buyerSequenceApi.log(buyerId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.stateForBuyer(buyerId) });
      void qc.invalidateQueries({ queryKey: communicationKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      void qc.invalidateQueries({ queryKey: activityKeys.byOwner("buyer_lead", buyerId) });
    },
  });
}

/** What an Undo attempt reports: the ids that could NOT be deleted. */
export interface UndoOutreachResult {
  failedIds: string[];
}

/**
 * Undo — the EXISTING `DELETE /communications/{id}`, once per entry the LOG
 * wrote. Settled rather than all-or-nothing, and the ids that failed come back,
 * so a retry deletes only what is still there instead of re-deleting what is
 * already gone. Invalidates on settle, not on success: a partial undo has still
 * moved the Communications tab, the rail marker and the buyer's Last contact.
 */
export function useUndoOutreachLog(buyerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryIds: string[]): Promise<UndoOutreachResult> => {
      const results = await Promise.allSettled(entryIds.map((id) => communicationsApi.delete(id)));
      return {
        failedIds: entryIds.filter((_, i) => results[i].status === "rejected"),
      };
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.stateForBuyer(buyerId) });
      void qc.invalidateQueries({ queryKey: communicationKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      void qc.invalidateQueries({ queryKey: activityKeys.byOwner("buyer_lead", buyerId) });
    },
  });
}
